import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  buildTenantUsersSearchParams,
  isTenantUsersStatusFilter,
  parseTenantUsersFilterFromSearchParams,
  type TenantUsersStatusFilter,
} from '../tenantUsersListParams';

export function useTenantUsersSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo(
    () => parseTenantUsersFilterFromSearchParams(searchParams),
    [searchParams],
  );

  const urlSearchValue = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearchValue);
  const [previousUrlSearchValue, setPreviousUrlSearchValue] =
    useState(urlSearchValue);

  if (urlSearchValue !== previousUrlSearchValue) {
    setPreviousUrlSearchValue(urlSearchValue);
    setSearchInput(urlSearchValue);
  }

  const replaceParams = useCallback(
    ({
      searchInput: nextSearchInput,
      status,
    }: {
      searchInput: string;
      status: TenantUsersStatusFilter;
    }) => {
      setSearchParams(
        buildTenantUsersSearchParams({
          searchInput: nextSearchInput,
          status,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      replaceParams({
        searchInput: value,
        status: filter.status,
      });
    },
    [filter.status, replaceParams],
  );

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      if (!isTenantUsersStatusFilter(value)) {
        return;
      }
      replaceParams({
        searchInput,
        status: value,
      });
    },
    [replaceParams, searchInput],
  );

  return {
    searchInput,
    handleSearchChange,
    statusFilter: filter.status,
    handleStatusFilterChange,
    debouncedSearch: filter.search,
  };
}
